import React, {useCallback, useEffect, useState, useRef} from 'react';
import AppContainer from '../Components/AppContainer';
import {StyleSheet, Linking} from 'react-native';
import {
  Box,
  Heading,
  Pressable,
  Skeleton,
  VStack,
  HStack,
  Stack,
  Text,
  Center,
  Spinner,
  IconButton,
  Input,
  Button,
  Badge,
  ScrollView,
  Checkbox,
} from 'native-base';
import main from '../../Assets/Styles/main.json';
import {createIconSetFromFontello} from 'react-native-vector-icons';
import lineAwesomeConfig from '../../Assets/Fontello/line-awesome-config.json';
import APIAction from '../../Actions/APIAction';
import GeneralAction from '../../Actions/GeneralAction';
import Trans from '../Components/Trans';
import {useIsFocused} from '@react-navigation/native';
import {format} from 'date-fns';
import DateTimePicker from '../../Libs/DateTimePicker';

const Icon = createIconSetFromFontello(lineAwesomeConfig);
const mainStyle = StyleSheet.create(main);

const ContractsOverviewScreen = ({navigation, route}) => {
  const {clientId, clientName} = route.params;
  const firstLoad = useRef(true);
  const focused = useIsFocused();

  const [contracts, setContracts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  // Date filters - default to today
  const today = new Date();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  // Multi-select for bulk download
  const [selectedContractIds, setSelectedContractIds] = useState([]);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  // Employee filtering
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Include non-shift contracts checkbox
  // When checked: also include contracts not linked to a specific shift (e.g., global NDAs)
  const [includeNonShiftContracts, setIncludeNonShiftContracts] = useState(false);

  const loadEmployees = useCallback(async (searchTerm = '') => {
    try {
      // Build URL with search parameter
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.length > 0) {
        params.append('search', searchTerm);
      }

      const url = `/api/clients/${clientId}/employees${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await APIAction.request({
        method: 'GET',
        url: url,
        cache: false,
      });

      if (!response || !response.data) {
        setAvailableEmployees([]);
        setShowEmployeeDropdown(false);
        return;
      }

      setAvailableEmployees(response.data);
      setShowEmployeeDropdown(true);
    } catch (error) {
      console.error('Failed to load employees:', error);
      setAvailableEmployees([]);
      setShowEmployeeDropdown(false);
    } finally {
      setLoadingEmployees(false);
    }
  }, [clientId]);

  const loadContracts = useCallback(async () => {
    try {
      setLoading(true);
      setSelectedContractIds([]); // Clear selection when reloading

      // Build query parameters based on filtering logic:
      // - If date range is set AND checkbox NOT checked: Only contracts linked to active shifts in date range
      // - If date range is set AND checkbox IS checked: Contracts with active shifts + contracts not linked to shifts
      // - If employee selected AND checkbox IS checked: All contracts for employee (including non-shift)
      // - If employee selected AND checkbox NOT checked: Contracts linked to a shift based on date range
      const params = new URLSearchParams();

      // Always include date range (defaults to today on the backend)
      if (fromDate) {
        params.append('fromDate', format(fromDate, 'yyyy-MM-dd'));
      }
      if (toDate) {
        params.append('toDate', format(toDate, 'yyyy-MM-dd'));
      }

      // Add employee filter if selected
      if (selectedEmployees.length > 0) {
        params.append('employees', selectedEmployees.map(e => e.id).join(','));
      }

      // Add includeNonShiftContracts parameter when checkbox is checked
      if (includeNonShiftContracts) {
        params.append('includeNonShiftContracts', 'true');
      }

      // Fetch contracts for this client with filters
      const response = await APIAction.request({
        method: 'GET',
        url: `/api/clients/${clientId}/signed-contracts?${params.toString()}`,
        cache: false,
      });

      if (!response || !response.data) {
        setContracts([]);
        return;
      }

      setContracts(response.data);
    } catch (error) {
      console.error('Failed to load contracts:', error);
      GeneralAction.toast(
        'error',
        <Trans>Failed to load contracts</Trans>,
      );
      setContracts([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, fromDate, toDate, selectedEmployees, includeNonShiftContracts]);

  const onFirstLoad = useCallback(() => {
    if (!focused) {
      return;
    }

    loadContracts();
  }, [focused, loadContracts]);

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      onFirstLoad();
    }
  }, [onFirstLoad]);

  useEffect(() => {
    if (!focused) {
      firstLoad.current = true;
    }
  }, [focused]);

  // Debounced employee search
  useEffect(() => {
    if (employeeSearchTerm.length === 0) {
      setAvailableEmployees([]);
      setShowEmployeeDropdown(false);
      setLoadingEmployees(false);
      return;
    }

    // Show loading spinner immediately when user starts typing
    setLoadingEmployees(true);

    // Only search if at least 3 characters
    if (employeeSearchTerm.length < 3) {
      setShowEmployeeDropdown(false);
      // Keep spinner showing while user is typing
      return;
    }

    const timer = setTimeout(() => {
      loadEmployees(employeeSearchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [employeeSearchTerm, loadEmployees]);

  const onDownloadContractHandler = async contractId => {
    try {
      setDownloadingId(contractId);

      const response = await APIAction.request({
        method: 'POST',
        url: `/api/contracts/${contractId}/download-link`,
        body: {
          expiresInHours: 24,
        },
      });

      if (!response || !response.url) {
        GeneralAction.toast(
          'error',
          <Trans>Failed to generate download link</Trans>,
        );
        return;
      }

      await Linking.openURL(response.url);
    } catch (error) {
      console.error('Failed to download contract:', error);
      GeneralAction.toast('error', <Trans>Failed to download contract</Trans>);
    } finally {
      setDownloadingId(null);
    }
  };

  const getTemplateName = documentTemplate => {
    if (!documentTemplate || !documentTemplate.name) {
      return 'Contract';
    }

    if (typeof documentTemplate.name === 'object') {
      return (
        documentTemplate.name.nl ||
        documentTemplate.name.en ||
        documentTemplate.name.fr ||
        Object.values(documentTemplate.name)[0] ||
        'Contract'
      );
    }

    return documentTemplate.name;
  };

  const formatDate = dateString => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const onApplyFiltersHandler = () => {
    loadContracts();
  };

  const onAddEmployeeHandler = employee => {
    if (!selectedEmployees.find(e => e.id === employee.id)) {
      setSelectedEmployees([...selectedEmployees, employee]);
    }
    setEmployeeSearchTerm('');
    setShowEmployeeDropdown(false);
  };

  const onRemoveEmployeeHandler = employeeId => {
    setSelectedEmployees(selectedEmployees.filter(e => e.id !== employeeId));
  };

  const onToggleContractHandler = contractId => {
    if (selectedContractIds.includes(contractId)) {
      setSelectedContractIds(selectedContractIds.filter(id => id !== contractId));
      return;
    }

    setSelectedContractIds([...selectedContractIds, contractId]);
  };

  const onSelectAllContractsHandler = () => {
    if (selectedContractIds.length === contracts.length) {
      setSelectedContractIds([]);
      return;
    }

    setSelectedContractIds(contracts.map(c => c.id));
  };

  const onBulkDownloadHandler = async () => {
    if (selectedContractIds.length === 0) {
      GeneralAction.toast(
        'warning',
        <Trans>Please select at least one contract</Trans>,
      );
      return;
    }

    try {
      setIsBulkDownloading(true);

      const response = await APIAction.request({
        method: 'POST',
        url: '/api/signed-contracts/bulk-download',
        body: {
          contractIds: selectedContractIds,
        },
      });

      // Check if it's an async response (status: processing)
      if (response && response.status === 'processing') {
        GeneralAction.toast(
          'info',
          <Trans>Your download is being prepared. You will receive an email when ready.</Trans>,
        );
        return;
      }

      // Check if download is ready (sync response)
      if (response && response.status === 'ready' && response.downloadUrl) {
        await Linking.openURL(response.downloadUrl);
        GeneralAction.toast(
          'success',
          <Trans>Download started</Trans>,
        );
        return;
      }

      GeneralAction.toast(
        'error',
        <Trans>Failed to start bulk download</Trans>,
      );
    } catch (error) {
      console.error('Failed to bulk download:', error);
      GeneralAction.toast(
        'error',
        <Trans>Failed to start bulk download</Trans>,
      );
    } finally {
      setIsBulkDownloading(false);
    }
  };

  // Filter out already selected employees from the search results
  const filteredEmployees = availableEmployees.filter(emp => {
    const isAlreadySelected = selectedEmployees.find(e => e.id === emp.id);
    return !isAlreadySelected;
  });

  return (
    <AppContainer>
      <VStack space={2}>
        <Box style={[mainStyle.pageTitleWrapper, {paddingBottom: 0}]}>
          <Box style={{flexGrow: 1}}>
            <HStack space={2} alignItems={'center'}>
              <Box>
                <IconButton
                  onPress={() => navigation.goBack()}
                  variant={'ghost'}
                  icon={<Icon name={'arrow-left'} size={24} color={'#000'} />}
                />
              </Box>
              <Box flexGrow={1} flexShrink={1}>
                <Heading style={mainStyle.pageTitle}>
                  <Trans>Signed Contracts</Trans>
                </Heading>
                <Text fontSize={'sm'} color={'gray.600'}>
                  {clientName}
                </Text>
              </Box>
            </HStack>
          </Box>
        </Box>

        {/* Filters Section */}
        <Box style={[mainStyle.whiteBox, {overflow: 'visible'}]}>
          <VStack space={3} style={{overflow: 'visible'}}>
            <Heading size={'sm'}>
              <Trans>Filters</Trans>
            </Heading>

            {/* Date Range Filters */}
            <HStack space={2}>
              <VStack flex={1} space={1}>
                <Text fontSize={'xs'} color={'gray.600'}>
                  <Trans>From Date</Trans>
                </Text>
                <DateTimePicker
                  mode="date"
                  value={fromDate}
                  onChange={setFromDate}
                  maximumDate={toDate}
                />
              </VStack>
              <VStack flex={1} space={1}>
                <Text fontSize={'xs'} color={'gray.600'}>
                  <Trans>To Date</Trans>
                </Text>
                <DateTimePicker
                  mode="date"
                  value={toDate}
                  onChange={setToDate}
                  minimumDate={fromDate}
                />
              </VStack>
            </HStack>

            {/* Include non-shift contracts checkbox */}
            <HStack space={2} alignItems={'center'}>
              <Checkbox
                value="includeNonShiftContracts"
                isChecked={includeNonShiftContracts}
                onChange={(isChecked) => setIncludeNonShiftContracts(isChecked)}
                colorScheme={'primary'}
              >
                <Text fontSize={'sm'} ml={2}>
                  <Trans>Include contracts not linked to shifts</Trans>
                </Text>
              </Checkbox>
            </HStack>
            <Text fontSize={'xs'} color={'gray.500'} mt={-1}>
              <Trans>When checked, also shows global contracts not tied to specific shifts</Trans>
            </Text>

            {/* Employee Filter */}
            <VStack space={1} overflow={'visible'} mb={(showEmployeeDropdown || loadingEmployees) ? 160 : 0}>
              <Text fontSize={'xs'} color={'gray.600'}>
                <Trans>Filter by Employees</Trans>
              </Text>

              {/* Selected Employees */}
              {selectedEmployees.length > 0 && (
                <HStack space={2} flexWrap={'wrap'} mb={2}>
                  {selectedEmployees.map(emp => (
                    <Pressable
                      key={emp.id}
                      onPress={() => onRemoveEmployeeHandler(emp.id)}
                      mb={1}>
                      <HStack
                        bg={'primary.500'}
                        px={3}
                        py={2}
                        borderRadius={20}
                        alignItems={'center'}
                        space={1}>
                        <Text color={'white'} fontSize={'sm'} fontWeight={'500'}>
                          {emp.firstName} {emp.lastName}
                        </Text>
                        <Icon name={'times'} size={12} color={'white'} />
                      </HStack>
                    </Pressable>
                  ))}
                </HStack>
              )}

              {/* Employee Search Input */}
              <Box position={'relative'}>
                <Input
                  value={employeeSearchTerm}
                  onChangeText={(text) => {
                    setEmployeeSearchTerm(text);
                    // Show spinner immediately when user types
                    if (text.length > 0) {
                      setLoadingEmployees(true);
                    } else {
                      setLoadingEmployees(false);
                    }
                  }}
                  onFocus={() => {
                    if (employeeSearchTerm.length >= 3 && availableEmployees.length > 0) {
                      setShowEmployeeDropdown(true);
                    }
                  }}
                  placeholder="Search employees..."
                  size={'md'}
                />

                {/* Employee Dropdown */}
                {(showEmployeeDropdown || loadingEmployees) && (
                  <Box
                    position={'absolute'}
                    top={'100%'}
                    left={0}
                    right={0}
                    bg={'white'}
                    borderWidth={1}
                    borderColor={'gray.300'}
                    borderRadius={4}
                    mt={1}
                    maxH={200}
                    zIndex={9999}
                    shadow={3}
                    style={{elevation: 5}}>
                    {loadingEmployees ? (
                      <Box p={3} alignItems={'center'}>
                        <Spinner size={'sm'} color={'primary.600'} />
                        <Text fontSize={'xs'} color={'gray.500'} mt={2}>
                          <Trans>Searching...</Trans>
                        </Text>
                      </Box>
                    ) : filteredEmployees.length > 0 ? (
                      <ScrollView>
                        {filteredEmployees.slice(0, 10).map(emp => (
                          <Pressable
                            key={emp.id}
                            onPress={() => onAddEmployeeHandler(emp)}
                            p={2}
                            borderBottomWidth={1}
                            borderBottomColor={'gray.100'}>
                            <Text>
                              {emp.firstName} {emp.lastName}
                            </Text>
                            {emp.email && (
                              <Text fontSize={'xs'} color={'gray.500'}>
                                {emp.email}
                              </Text>
                            )}
                          </Pressable>
                        ))}
                      </ScrollView>
                    ) : (
                      <Box p={3} alignItems={'center'}>
                        <Text fontSize={'xs'} color={'gray.500'}>
                          <Trans>No employees found</Trans>
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </VStack>

            {/* Apply Filters Button */}
            <Button onPress={onApplyFiltersHandler} colorScheme={'primary'}>
              <HStack space={2} alignItems={'center'}>
                <Icon name={'filter'} size={16} color={'white'} />
                <Text color={'white'} fontWeight={'600'}>
                  <Trans>Apply Filters</Trans>
                </Text>
              </HStack>
            </Button>
          </VStack>
        </Box>

        {/* Contracts List */}
        <Box style={mainStyle.whiteBox}>
          <Stack
            direction={{base: 'column', md: 'row'}}
            justifyContent={{base: 'flex-start', md: 'space-between'}}
            alignItems={{base: 'flex-start', md: 'center'}}
            space={2}
            mb={3}>
            <Heading size={'sm'}>
              <Trans>Contracts</Trans>
              {contracts && contracts.length > 0 && (
                <Text fontSize={'sm'} color={'gray.500'} fontWeight={'normal'}>
                  {' '}
                  ({contracts.length})
                </Text>
              )}
            </Heading>
            {contracts && contracts.length > 0 && (
              <HStack space={2} flexWrap={'wrap'}>
                <Button
                  size={'sm'}
                  variant={'outline'}
                  onPress={onSelectAllContractsHandler}>
                  <Text fontSize={'xs'}>
                    {selectedContractIds.length === contracts.length ? (
                      <Trans>Deselect All</Trans>
                    ) : (
                      <Trans>Select All</Trans>
                    )}
                  </Text>
                </Button>
                {selectedContractIds.length > 0 && (
                  <Button
                    size={'sm'}
                    colorScheme={'primary'}
                    onPress={onBulkDownloadHandler}
                    isLoading={isBulkDownloading}
                    isDisabled={isBulkDownloading}>
                    <HStack space={1} alignItems={'center'}>
                      <Icon name={'download'} size={14} color={'white'} />
                      <Text color={'white'} fontSize={'xs'} fontWeight={'600'}>
                        <Trans>Download</Trans> ({selectedContractIds.length})
                      </Text>
                    </HStack>
                  </Button>
                )}
              </HStack>
            )}
          </Stack>

          {loading ? (
            <VStack space={2}>
              <Skeleton h={'24'} />
              <Skeleton h={'24'} />
              <Skeleton h={'24'} />
            </VStack>
          ) : contracts && contracts.length > 0 ? (
            <VStack space={2}>
              {contracts.map(contract => (
                <Box
                  key={contract.id}
                  style={{
                    padding: 16,
                    backgroundColor: selectedContractIds.includes(contract.id)
                      ? '#e3f2fd'
                      : '#f8f9fa',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: selectedContractIds.includes(contract.id)
                      ? '#2196f3'
                      : '#e9ecef',
                  }}>
                  <HStack space={3} alignItems={'center'}>
                    <Checkbox
                      value={contract.id}
                      isChecked={selectedContractIds.includes(contract.id)}
                      onChange={() => onToggleContractHandler(contract.id)}
                      colorScheme={'primary'}
                    />
                    <Pressable
                      onPress={() => onDownloadContractHandler(contract.id)}
                      disabled={downloadingId !== null}
                      style={{flexDirection: 'row', flex: 1, alignItems: 'center'}}>
                      <Box
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 8,
                          backgroundColor: '#28a745',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}>
                        <Icon name={'file-contract'} size={20} color={'#fff'} />
                      </Box>
                      <Box flexGrow={1} flexShrink={1} ml={3}>
                        {contract.employee && (
                          <Text fontSize={'md'} fontWeight={'600'}>
                            {contract.employee.firstName}{' '}
                            {contract.employee.lastName}
                          </Text>
                        )}
                        <Text fontSize={'sm'} color={'gray.700'}>
                          {getTemplateName(contract.documentTemplate)}
                        </Text>
                        {contract.signedAt && (
                          <Text fontSize={'xs'} color={'gray.500'}>
                            <Trans>Signed</Trans>: {formatDate(contract.signedAt)}
                          </Text>
                        )}
                        {contract.file && contract.file.fileName && (
                          <Text fontSize={'xs'} color={'gray.500'}>
                            {contract.file.fileName}
                          </Text>
                        )}
                      </Box>
                      <Box>
                        {downloadingId === contract.id ? (
                          <Spinner size={'sm'} color={'primary.600'} />
                        ) : (
                          <Icon
                            name={'download'}
                            size={24}
                            color={'#007bff'}
                          />
                        )}
                      </Box>
                    </Pressable>
                  </HStack>
                </Box>
              ))}
            </VStack>
          ) : (
            <Center py={10}>
              <Icon
                name={'file-contract'}
                size={48}
                color={'#dee2e6'}
                style={{marginBottom: 10}}
              />
              <Text fontSize={'md'} color={'gray.500'}>
                <Trans>No signed contracts found</Trans>
              </Text>
              <Text fontSize={'sm'} color={'gray.400'} textAlign={'center'} mt={2}>
                <Trans>Try adjusting your filters or date range</Trans>
              </Text>
            </Center>
          )}
        </Box>
      </VStack>
    </AppContainer>
  );
};

export default ContractsOverviewScreen;
